/**
 * Global console override utility
 * 
 * This utility overrides console methods to disable logging in production environments
 * while preserving normal behavior in development and staging.
 * 
 * Import this file once in your app's entry point.
 */

// Store original console methods
const originalConsole = {
    log: console.log,
    info: console.info,
    warn: console.warn,
    debug: console.debug,
    error: console.error
};

/**
 * Determines if the current environment is a browser (not server-side)
 */
const isBrowser = (): boolean => {
    return typeof window !== 'undefined';
};

/**
 * Determines if the current environment should show logs
 * Shows logs on localhost and staging, hides them in production
 */
const shouldShowLogs = (): boolean => {
    // If we're not in a browser, we're in server-side rendering
    if (!isBrowser()) return true;

    const hostname = window.location.hostname;

    // Allow logs on localhost and staging
    return (
        hostname.includes('localhost') ||
        hostname.includes('127.0.0.1') ||
        hostname.includes('staging') ||
        hostname.includes('pro-staging')
    );
};

/**
 * Override console methods to disable logging in production
 */
export function setupConsoleOverrides(): void {
    // Don't override in server environment
    if (!isBrowser()) return;

    // If we should show logs, don't override anything
    if (shouldShowLogs()) return;

    // In production, override console methods with empty functions
    // except for error which we'll keep for critical issues
    console.log = () => { };
    console.info = () => { };
    console.warn = () => { };
    console.debug = () => { };

    // Optionally keep error logging even in production
    // Comment this line if you want to silence errors too
    // console.error = () => {};
}

/**
 * Restore original console behavior
 * Useful for testing or if you need to temporarily restore logging
 */
export function restoreConsole(): void {
    if (!isBrowser()) return;

    console.log = originalConsole.log;
    console.info = originalConsole.info;
    console.warn = originalConsole.warn;
    console.debug = originalConsole.debug;
    console.error = originalConsole.error;
} 